/**
 * Pipeline schema for the data transform tool.
 *
 * A pipeline is an ordered array of actions. Each action is a flat object
 * tagged with a single-value `type` enum; `actionSchema` is the union of all
 * action shapes. Datasets flow left-to-right — each action receives the
 * previous action's output as its input.
 *
 * Column references (including derive output names referenced by later
 * actions) go through the shared `columnSchema` / `columnsSchema` from
 * `column-ref.ts` so the LLM sees one consistent contract for "name a column
 * or write a math.js expression."
 *
 * Schema design constraints (LLM compatibility):
 * - Tagged unions use `z.union` of objects with single-value `z.enum(['type'])`
 *   discriminators (anyOf in JSON Schema, broadly supported)
 * - No `z.discriminatedUnion`, no `z.literal()`, no `z.lazy()`
 */
import z from 'zod';

import { columnSchema, columnsSchema } from './column-ref.ts';
import { rootFilterSchema } from '#plugin-chat/lib/data-pipeline/filter/schema.ts';

/** Aggregation operations available to `rollup` and `pivot`. */
const aggregateOpSchema = z.enum(['count', 'sum', 'min', 'max', 'avg', 'first', 'last']);

/**
 * Upper bound on `limit` values for slice / spread / unroll actions. Values
 * above this are almost certainly the LLM treating `limit` as required and
 * sentinel-encoding "no limit" (e.g. `Number.MAX_SAFE_INTEGER`). Rejecting
 * here points the LLM at the right form: omit the field instead.
 */
export const MAX_ACTION_LIMIT = 100_000;

/** Shared schema for the `limit` field on slice / spread / unroll. */
const actionLimitSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_ACTION_LIMIT, {
    message: `limit must be at most ${MAX_ACTION_LIMIT}. To return all rows / produce all columns, omit the \`limit\` field entirely — do not pass a sentinel value like Number.MAX_SAFE_INTEGER.`,
  })
  .optional();
export type AggregateOp = z.infer<typeof aggregateOpSchema>;

/** All supported action types — keep tight, every entry adds LLM choice burden. */
export const ACTION_TYPES = [
  'filter',
  'derive',
  'sort',
  'slice',
  'rollup',
  'pivot',
  'fold',
  'spread',
  'unroll',
  'expandTimeseries',
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

// Sub-specs reused across action shapes.

const deriveSpecSchema = z.object({
  name: z.string().describe('Output column name for this computed field'),
  expression: z.string().describe(
    'Math.js-compatible expression. Column names must be valid identifiers ' +
      '(use row["awkward.name"] bracket-access for non-identifier columns). ',
    // 'See the transformDataset instructions for the full math.js function reference.',
  ),
});
const sortKeySchema = z.object({
  column: columnSchema,
  direction: z.enum(['asc', 'desc']).default('asc').describe('Sort direction (default: asc)'),
});
export type SortKey = z.infer<typeof sortKeySchema>;

const aggregationSpecSchema = z.object({
  name: z.string().describe('Output column name for the aggregation result'),
  op: aggregateOpSchema.describe('Aggregation operation to perform'),
  column: columnSchema
    .optional()
    .describe('Column to aggregate. Required for sum/min/max/avg/first/last; optional for count.'),
});
export type AggregationSpec = z.infer<typeof aggregationSpecSchema>;

// Action sub-schemas — one per `type`.

const filterAction = z.object({
  type: z.literal('filter').describe('Keep rows that satisfy a predicate.'),
  where: rootFilterSchema,
});
export type FilterAction = z.infer<typeof filterAction>;

const deriveAction = z.object({
  type: z.literal('derive').describe('Add computed columns via math.js expressions.'),
  derives: z.array(deriveSpecSchema).min(1).describe('Computed columns to add'),
});
export type DeriveAction = z.infer<typeof deriveAction>;

const sortAction = z.object({
  type: z.literal('sort').describe('Order rows by one or more keys.'),
  keys: z.array(sortKeySchema).min(1).describe('Sort keys, applied in declared order'),
});
export type SortAction = z.infer<typeof sortAction>;

const sliceAction = z.object({
  type: z.literal('slice').describe('Row-level pagination — offset then limit.'),
  limit: actionLimitSchema.describe(
    `Maximum number of rows to keep. Zero returns an empty result. Omit entirely to keep all rows — do NOT pass a large sentinel like Number.MAX_SAFE_INTEGER. Hard cap: ${MAX_ACTION_LIMIT}.`,
  ),
  offset: z.number().int().min(0).optional().describe('Number of rows to skip before applying limit. Defaults to 0.'),
});
export type SliceAction = z.infer<typeof sliceAction>;

const rollupAction = z.object({
  type: z
    .literal('rollup')
    .describe('Compute aggregations, optionally grouped by one or more columns. Omit `columns` for a global rollup.'),
  columns: columnsSchema.optional().describe('Group keys. Omit to roll up the whole dataset into a single row.'),
  aggregations: z.array(aggregationSpecSchema).min(1).describe('Aggregations to compute per group'),
});
export type RollupAction = z.infer<typeof rollupAction>;

const pivotAction = z.object({
  type: z
    .literal('pivot')
    .describe('Long → wide reshape. Unique values in keyColumn become new column names; valueColumn fills the cells.'),
  keyColumn: columnSchema.describe('Column whose unique values become the new column names'),
  valueColumn: columnSchema.describe('Column whose values fill the new columns'),
  aggregation: aggregateOpSchema
    .optional()
    .describe('Aggregation when multiple values map to the same cell (default: first)'),
});
export type PivotAction = z.infer<typeof pivotAction>;

const foldAction = z.object({
  type: z.literal('fold').describe('Wide → long reshape. The named columns become key-value rows.'),
  columns: columnsSchema.describe('Columns to fold into key-value pairs'),
  keyName: z.string().optional().describe('Name for the output key column (default: "key")'),
  valueName: z.string().optional().describe('Name for the output value column (default: "value")'),
});
export type FoldAction = z.infer<typeof foldAction>;

const spreadAction = z.object({
  type: z.literal('spread').describe('Expand an array column into multiple columns (one per array index).'),
  column: columnSchema.describe('Column containing arrays to expand'),
  limit: actionLimitSchema.describe(
    `Maximum number of output columns. Omit entirely to fan out every array element — do NOT pass a sentinel value. Hard cap: ${MAX_ACTION_LIMIT}.`,
  ),
});
export type SpreadAction = z.infer<typeof spreadAction>;

const unrollAction = z.object({
  type: z.literal('unroll').describe('Expand an array column into multiple rows (one per array element).'),
  column: columnSchema.describe('Column containing arrays to expand'),
  indexName: z.string().optional().describe('Optional column name for the array index'),
  limit: actionLimitSchema.describe(
    `Maximum number of rows per source row. Omit entirely to unroll every array element — do NOT pass a sentinel value. Hard cap: ${MAX_ACTION_LIMIT}.`,
  ),
});
export type UnrollAction = z.infer<typeof unrollAction>;
const expandTimeseriesAction = z.object({
  type: z
    .literal('expandTimeseries')
    .describe(
      'Reshape datapoint-array columns into a wide-format chart-ready dataset. ' +
        'Output: rows with a `timestamp` column plus one numeric column per series. ' +
        'Use this as the final action in a pipeline whose output is destined for showDashboard.',
    ),
  columns: columnsSchema.describe(
    'Datapoint-array columns to expand into series. Each must hold arrays of {timestamp, value|...} objects ' +
      '(output of datapoints()/hourly()/daily()/weekly()/monthly() derives).',
  ),
  splitBy: columnSchema
    .optional()
    .describe(
      "Column whose value identifies each row's series. Required when the input has more than one row. " +
        'With one input column, splitBy values become the output column names; with multiple input columns, ' +
        'output columns are named `<splitBy>__<column>`.',
    ),
  align: z
    .enum(['union', 'inner'])
    .optional()
    .describe(
      'Timestamp alignment across series. "union" (default) keeps every timestamp from any series and fills ' +
        'gaps with null. "inner" keeps only timestamps present in every series.',
    ),
});

export type ExpandTimeseriesAction = z.infer<typeof expandTimeseriesAction>;

// Action union and pipeline shape.

export const actionSchema = z.union([
  filterAction,
  deriveAction,
  sortAction,
  sliceAction,
  rollupAction,
  pivotAction,
  foldAction,
  spreadAction,
  unrollAction,
  expandTimeseriesAction,
]);

export type Action = z.infer<typeof actionSchema>;

export const pipelineSchema = z.object({
  actions: actionSchema
    .array()
    .describe(
      'Ordered pipeline of chained actions (action output is piped to the next action as input) to apply to the input dataset.',
    ),
});

export type PipelineSpec = z.infer<typeof pipelineSchema>;

/** Subset of an action with a known `type` — narrowed by `type` enum lookup. */
export type ActionOfType<T extends ActionType> = Extract<Action, { type: T }>;
